import { Button } from "@/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@primitives/dropdown-menu";

import { ImagePlay, File, CirclePlus } from "lucide-react";
import { useRef, useEffect } from "react";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";

import { ALLOWED_DOC_TYPES, ALLOWED_MEDIA_TYPES } from "@/config/constants";

/**
 * Purpose: Desktop-specific attachment menu component
 *
 * How it works:
 * 1. Provides a dropdown menu with separate options for media and files
 * 2. Uses separate file inputs for different file types
 * 3. Validates file types and triggers appropriate handlers
 * 4. Resets inputs when attachments are processed
 *
 * Key features:
 * - Dropdown menu with separate options
 * - Type-specific file inputs
 * - File type validation with error feedback
 * - Input reset when attachments are processed
 */
export default function DesktopAttachmentMenu() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleMediaAttachment, newMediaAttachment } =
    useMediaMessageSenderState();

  const { handleFileAttachment, newFileAttachment } =
    useFileMessageSenderState();

  const mediaInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset file inputs when dialogs close
  useEffect(() => {
    if (!newFileAttachment && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [newFileAttachment]);

  useEffect(() => {
    if (!newMediaAttachment && mediaInputRef.current) {
      mediaInputRef.current.value = "";
    }
  }, [newMediaAttachment]);

  const handleMediaInputClick = () => {
    mediaInputRef.current?.click();
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleMediaAttachmentInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const isMedia = ALLOWED_MEDIA_TYPES.includes(file.type);
    if (!isMedia) {
      toast({
        duration: 1000,
        description: t("fileTypeNotSupported"),
        variant: "destructive",
      });
      return;
    }
    handleMediaAttachment(file);
  };

  const handleFileAttachmentInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const isPdf = ALLOWED_DOC_TYPES.includes(file.type);
    if (!isPdf) {
      toast({
        duration: 1000,
        description: t("fileTypeNotSupported"),
        variant: "destructive",
      });
      return;
    }
    handleFileAttachment(file);
  };

  return (
    <>
      <input
        type="file"
        name="media"
        accept={ALLOWED_MEDIA_TYPES.join(", ")}
        ref={mediaInputRef}
        className="hidden"
        onChange={handleMediaAttachmentInputChange}
      />
      <input
        type="file"
        name="file"
        accept={ALLOWED_DOC_TYPES.join(", ")}
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileAttachmentInputChange}
      />

      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center">
          <Button
            asChild
            className="size-5 shrink-0 lg:cursor-pointer"
            size="icon"
            variant="ghost"
          >
            <CirclePlus />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="flex flex-col gap-0">
          <DropdownMenuItem
            onClick={handleMediaInputClick}
            className="flex items-center lg:cursor-pointer"
          >
            <ImagePlay className="size-4" /> {t("attachmentMenu.photoOrVideo")}
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={handleFileInputClick}
            className="flex items-center lg:cursor-pointer"
          >
            <File className="size-4" />
            {t("attachmentMenu.file")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
